import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { cvmCasesHref } from '@/pages/cvm/OverviewPage';
import { Figure, TD, TD_NUM, TH, TH_NUM, THEAD, caseHref } from '@/pages/cvm/shared';
import { fetchCvmDirector } from '@/services/cvm';
import type { CvmDirectorProfile } from '@/services/types';

/**
 * One director, presidente or CRSFN conselheiro, by the slug of the canonical name.
 *
 * Judgments reported and positions recorded, by year; the provisions and
 * sanction types on the judgments this person reported, as counts; the most
 * recent judgments. The rate card is a refusal computed per request: a
 * conviction, acquittal or follow rate would read as a disposition and the
 * relator is drawn by lot within a case mix this corpus cannot control for.
 */
export function CvmDirectorPage() {
  const { slug = '' } = useParams();
  const { t } = useTranslation(['cvm', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cvm', 'director', slug],
    queryFn: () => fetchCvmDirector(slug),
    enabled: slug.length > 0,
  });

  if (isPending) return <SkeletonLoader rows={8} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  const name = data.profile.value?.name ?? slug;
  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/cvm/directors" className="underline-offset-2 hover:underline">
            {t('cvm:director.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">{name}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:director.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:director.explainer" />
      <AsOfStrip asOf={data.as_of} />

      <Answer answer={data.profile} help={t('cvm:director.profile.help')} scope="record">
        {(p: CvmDirectorProfile) => <Profile profile={p} />}
      </Answer>

      <RefusalCard title={t('cvm:director.rates.title')} answer={data.rates} />

      {data.profile.value ? (
        <p className="text-xs text-muted-foreground">
          {/* The case list filters on the canonical NAME, which is what the
              filter options there list; the slug is only this page's address. */}
          <Link
            to={cvmCasesHref({ relator: data.profile.value.name })}
            className="text-primary underline-offset-2 hover:underline"
          >
            {t('cvm:director.openCases', { name })}
          </Link>
        </p>
      ) : null}

      <FieldGuide i18nKey="cvm:director.guide" />
    </div>
  );
}

function Profile({ profile }: { profile: CvmDirectorProfile }) {
  const { t, i18n } = useTranslation(['cvm']);
  const number = new Intl.NumberFormat(i18n.language);
  const positions = Object.entries(profile.positions).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-card p-4" data-profile>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Figure
            label={t('cvm:director.profile.body')}
            value={t(`cvm:bodies.${profile.body}`, {
              defaultValue: profile.body,
            })}
            hint={profile.role ?? undefined}
          />
          <Figure
            label={t('cvm:director.profile.relatorDecisions')}
            value={number.format(profile.relator_decisions)}
          />
          <Figure
            label={t('cvm:director.profile.voteRows')}
            value={number.format(profile.vote_rows)}
          />
          <Figure
            label={t('cvm:director.profile.aliases')}
            value={number.format(profile.aliases.length)}
            hint={profile.aliases.join('; ')}
          />
        </dl>
        <dl className="mt-3 flex flex-wrap gap-2 [&>div]:min-w-[10rem] [&>div]:flex-1">
          {positions.map(([code, count]) => (
            <Figure
              key={code}
              label={t(`cvm:positions.${code}`, { defaultValue: code })}
              value={number.format(count)}
            />
          ))}
        </dl>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-years>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:director.years.title')}
          </h2>
          <table className="w-full text-sm">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>{t('cvm:director.years.year')}</th>
                <th className={TH_NUM}>{t('cvm:director.years.relator')}</th>
                <th className={TH_NUM}>{t('cvm:director.years.rows')}</th>
                <th className={TH_NUM}>{t('cvm:director.years.dissents')}</th>
              </tr>
            </thead>
            <tbody>
              {profile.years.map((y) => (
                <tr key={y.year} className="border-t">
                  <td className={TD}>{y.year}</td>
                  <td className={TD_NUM}>{number.format(y.relator_decisions)}</td>
                  <td className={TD_NUM}>{number.format(y.vote_rows)}</td>
                  <td className={TD_NUM}>{number.format(y.dissents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="rounded-lg border bg-card p-4" data-as-relator>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:director.asRelator.title')}
          </h2>
          <p className="mb-2 text-xs text-muted-foreground">{t('cvm:director.asRelator.help')}</p>
          <dl className="flex flex-wrap gap-2 [&>div]:min-w-[10rem] [&>div]:flex-1">
            {Object.entries(profile.sanction_types_as_relator)
              .sort((a, b) => b[1] - a[1])
              .map(([code, count]) => (
                <Figure
                  key={code}
                  label={t(`cvm:sanctionTypes.${code}`, { defaultValue: code })}
                  value={number.format(count)}
                />
              ))}
          </dl>
          {profile.provisions_as_relator.length ? (
            <ul className="mt-3 space-y-1 text-xs">
              {profile.provisions_as_relator.map((p) => (
                <li key={p.value} className="flex justify-between gap-2 border-t pt-1">
                  <span className="text-foreground">{p.value}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {number.format(p.count)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4" data-recent>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:director.recent.title')}
        </h2>
        {profile.recent.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:director.recent.none')}</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {profile.recent.map((r) => (
              <li key={r.case_key} className="flex flex-wrap justify-between gap-2 border-t pt-1">
                <Link
                  to={caseHref(r.case_key)}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {r.process_number}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {r.judgment_date ?? '—'}
                  {r.vote_mode_overall
                    ? ` · ${t(`cvm:voteModes.${r.vote_mode_overall}`, { defaultValue: r.vote_mode_overall })}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
